import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import {
  ArrowDownToLine,
  Bug,
  ChevronDown,
  Clock3,
  ExternalLink,
  FileDiff,
  FolderTree,
  GitCommitHorizontal,
  RefreshCw,
  UserRound,
} from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { EmptyState } from '@/Modules/Core/Components/Layout/EmptyState';
import FileChangeDetailCard from '@/Modules/Core/Components/FileChangeFeed/FileChangeDetailCard';
import FileChangeFeedSkeleton from '@/Modules/Core/Components/FileChangeFeed/FileChangeFeedSkeleton';
import FileChangeGroupCard from '@/Modules/Core/Components/FileChangeFeed/FileChangeGroupCard';
import FileChangeItemCard from '@/Modules/Core/Components/FileChangeFeed/FileChangeItemCard';
import FileChangeToolbar from '@/Modules/Core/Components/FileChangeFeed/FileChangeToolbar';
import {
  getActionMeta,
  summarizeItems,
  toTimestamp,
} from '@/Modules/Core/Components/FileChangeFeed/fileChangeFeedUtils';
import useAffectedFilesCatalog from '../hooks/useAffectedFilesCatalog';

const INITIAL_VISIBLE_FILES = 20;
const SEARCH_DEBOUNCE_DELAY = 700;
const VISIBLE_FILES_STEP = 20;

const BUG_FILTER_OPTIONS = [
  { id: 'jira', label: 'JDA bugs' },
  { id: 'deletions', label: 'Deletions' },
];

const getBugFilterClassName = (activeFilter, filterId) => {
  const isActive = activeFilter === filterId;

  if (!isActive) {
    return 'border-border/40 bg-transparent text-muted-foreground hover:border-border/60 hover:text-foreground';
  }

  if (filterId === 'deletions') {
    return 'border-rose-400/45 bg-rose-500/12 text-rose-700 shadow-sm dark:text-rose-200';
  }

  return 'border-primary/35 bg-primary/12 text-primary shadow-sm dark:text-primary-foreground';
};

const getBugType = (bugId) => {
  const normalized = String(bugId ?? '').trim().toUpperCase();

  if (!normalized || normalized === 'UNASSIGNED') {
    return 'unassigned';
  }

  if (normalized.startsWith('BY-')) {
    return 'by';
  }

  if (/^[A-Z][A-Z0-9]+-\d+$/.test(normalized)) {
    return 'jira';
  }

  return 'other';
};

const getBugSummaryId = (value) => String(value ?? '').trim();

const getBugSummaryCountForFilter = (summaryItem, bugFilter) => {
  const countValue =
    bugFilter === 'deletions'
      ? Number(summaryItem?.deletionCount)
      : Number(summaryItem?.matchedFileCount);

  return Number.isFinite(countValue) ? countValue : null;
};

const filterBugSummaries = (bugSummaries = [], bugFilter = 'all') =>
  bugSummaries.filter((summaryItem) => {
    const bugType = getBugType(summaryItem?.bugId);

    if (bugFilter === 'by') {
      return bugType === 'by';
    }

    if (bugFilter === 'jira') {
      return bugType === 'jira';
    }

    if (bugFilter === 'deletions') {
      return Number(summaryItem?.deletionCount) > 0;
    }

    return true;
  });

const groupAffectedFiles = (files, bugFilter, bugSummaryMap = new Map()) => {
  const grouped = new Map();

  files.forEach((file) => {
    const bugId = file.bugId || 'Unassigned';
    const bugKey = bugId || `bug-${file.key}`;
    const bugType = getBugType(bugId);

    if (bugFilter === 'by' && bugType !== 'by') {
      return;
    }

    if (bugFilter === 'jira' && bugType !== 'jira') {
      return;
    }

    const actionMeta = getActionMeta(file.action);

    if (bugFilter === 'deletions' && actionMeta.tone !== 'delete') {
      return;
    }

    if (!grouped.has(bugKey)) {
      grouped.set(bugKey, {
        id: bugKey,
        bugId,
        bugUrl: file.bugUrl,
        files: [],
        repos: new Set(),
        authors: new Set(),
        deletionCount: 0,
        latestCommitDate: '',
      });
    }

    const group = grouped.get(bugKey);
    group.files.push(file);
    group.bugUrl = group.bugUrl || file.bugUrl;

    if (file.repoName) {
      group.repos.add(file.repoName);
    }

    if (file.author) {
      group.authors.add(file.author);
    }

    if (actionMeta.tone === 'delete') {
      group.deletionCount += 1;
    }

    if (toTimestamp(file.commitDate) > toTimestamp(group.latestCommitDate)) {
      group.latestCommitDate = file.commitDate;
    }
  });

  return Array.from(grouped.values())
    .map((group) => {
      const sortedFiles = [...group.files].sort((first, second) => {
        const dateDifference = toTimestamp(second.commitDate) - toTimestamp(first.commitDate);

        if (dateDifference !== 0) {
          return dateDifference;
        }

        return String(first.filePath || '').localeCompare(String(second.filePath || ''));
      });
      const summaryItem = bugSummaryMap.get(getBugSummaryId(group.bugId));
      const reportedFileCount = getBugSummaryCountForFilter(summaryItem, bugFilter);
      const totalFilesForBug = Math.max(sortedFiles.length, reportedFileCount ?? sortedFiles.length);

      return {
        ...group,
        bugUrl: group.bugUrl || summaryItem?.bugUrl,
        files: sortedFiles,
        fileCount: sortedFiles.length,
        totalFilesForBug,
        repoSummary: summarizeItems(group.repos),
        authorSummary: summarizeItems(group.authors),
        deletionCount:
          Number.isFinite(Number(summaryItem?.deletionCount))
            ? Number(summaryItem.deletionCount)
            : group.deletionCount,
        latestCommitDateLabel: sortedFiles[0]?.commitDateLabel || 'Unknown date',
      };
    })
    .filter(Boolean)
    .sort((first, second) => {
      const dateDifference =
        toTimestamp(second.latestCommitDate) - toTimestamp(first.latestCommitDate);

      if (dateDifference !== 0) {
        return dateDifference;
      }

      return String(first.bugId || '').localeCompare(String(second.bugId || ''));
    });
};

const getAvailableBugFilterOptions = (files = [], bugSummaries = []) => {
  const availableFilterIds = new Set(['all']);

  files.forEach((file) => {
    const bugType = getBugType(file?.bugId);
    const actionMeta = getActionMeta(file?.action);

    if (bugType === 'by') {
      availableFilterIds.add('by');
    }

    if (bugType === 'jira') {
      availableFilterIds.add('jira');
    }

    if (actionMeta.tone === 'delete') {
      availableFilterIds.add('deletions');
    }
  });

  bugSummaries.forEach((summaryItem) => {
    const bugType = getBugType(summaryItem?.bugId);

    if (bugType === 'by') {
      availableFilterIds.add('by');
    }

    if (bugType === 'jira') {
      availableFilterIds.add('jira');
    }

    if (Number(summaryItem?.deletionCount) > 0) {
      availableFilterIds.add('deletions');
    }
  });

  return BUG_FILTER_OPTIONS.filter((option) =>
    availableFilterIds.has(option.id)
  );
};

const AffectedFiles = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [bugFilter, setBugFilter] = React.useState('all');
  const [expandedBugs, setExpandedBugs] = React.useState({});
  const [expandedFiles, setExpandedFiles] = React.useState({});
  const [visibleFilesByBug, setVisibleFilesByBug] = React.useState({});
  const [loadingMoreFilesByBug, setLoadingMoreFilesByBug] = React.useState({});
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
  const [alertMessage, setAlertMessage] = React.useState({
    type: 'error',
    message: '',
  });

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm((current) => (
        current === searchTerm ? current : searchTerm
      ));
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const {
    affectedFiles,
    bugSummary,
    errorMessage,
    hasMore,
    loading,
    loadingMore,
    refreshing,
    loadMoreAffectedFiles,
    loadMoreFilesForBug,
    refreshAffectedFiles,
    reportedTotal,
  } = useAffectedFilesCatalog({
    searchTerm: debouncedSearchTerm,
  });

  const clearAlert = React.useCallback(() => {
    setAlertMessage({
      type: 'error',
      message: '',
    });
  }, []);

  const showAlert = React.useCallback((message, type = 'error') => {
    setAlertMessage({
      type,
      message,
    });
  }, []);

  React.useEffect(() => {
    if (errorMessage) {
      showAlert(errorMessage);
    } else {
      clearAlert();
    }
  }, [clearAlert, errorMessage, showAlert]);

  const bugSummaryEntries = React.useMemo(
    () => (Array.isArray(bugSummary?.bugSummaries) ? bugSummary.bugSummaries : []),
    [bugSummary]
  );
  const bugSummaryMap = React.useMemo(
    () =>
      new Map(
        bugSummaryEntries.map((summaryItem) => [getBugSummaryId(summaryItem?.bugId), summaryItem])
      ),
    [bugSummaryEntries]
  );
  const filteredBugSummaries = React.useMemo(
    () => filterBugSummaries(bugSummaryEntries, bugFilter),
    [bugSummaryEntries, bugFilter]
  );
  const bugGroups = React.useMemo(
    () => groupAffectedFiles(affectedFiles, bugFilter, bugSummaryMap),
    [affectedFiles, bugFilter, bugSummaryMap]
  );
  const availableBugFilterOptions = React.useMemo(
    () => getAvailableBugFilterOptions(affectedFiles, bugSummaryEntries),
    [affectedFiles, bugSummaryEntries]
  );
  const normalizedSearchTerm = React.useMemo(
    () => String(debouncedSearchTerm ?? '').trim(),
    [debouncedSearchTerm]
  );

  React.useEffect(() => {
    if (
      !availableBugFilterOptions.some((option) => option.id === bugFilter)
    ) {
      setBugFilter('all');
    }
  }, [availableBugFilterOptions, bugFilter]);

  React.useEffect(() => {
    const visibleBugIds = new Set(bugGroups.map((group) => group.id));

    setExpandedBugs((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([bugId, isOpen]) => visibleBugIds.has(bugId) && isOpen)
      )
    );

    setExpandedFiles((current) => {
      const visibleFileIds = new Set(
        bugGroups.flatMap((group) => group.files.map((file) => String(file.key)))
      );

      return Object.fromEntries(
        Object.entries(current).filter(([fileId, isOpen]) => visibleFileIds.has(fileId) && isOpen)
      );
    });

    setVisibleFilesByBug((current) => {
      const nextState = {};

      bugGroups.forEach((group) => {
        nextState[group.id] = Math.min(
          current[group.id] ?? INITIAL_VISIBLE_FILES,
          group.fileCount
        );
      });

      return nextState;
    });

    setLoadingMoreFilesByBug((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([bugId, isLoading]) => visibleBugIds.has(bugId) && isLoading)
      )
    );
  }, [bugGroups]);

  React.useEffect(() => {
    if (!normalizedSearchTerm || bugGroups.length === 0) {
      return;
    }

    const firstBugGroup = bugGroups[0];

    setExpandedBugs((current) => ({
      ...current,
      [firstBugGroup.id]: true,
    }));

    setVisibleFilesByBug((current) => ({
      ...current,
      [firstBugGroup.id]: Math.min(
        current[firstBugGroup.id] ?? INITIAL_VISIBLE_FILES,
        firstBugGroup.fileCount
      ),
    }));
  }, [bugGroups, normalizedSearchTerm]);

  const visibleBugGroups = bugGroups;
  const isLoadingAnyBugFiles = React.useMemo(
    () => Object.values(loadingMoreFilesByBug).some(Boolean),
    [loadingMoreFilesByBug]
  );

  const filteredFileCount = React.useMemo(
    () => bugGroups.reduce((total, group) => total + group.fileCount, 0),
    [bugGroups]
  );
  const totalFilesForActiveFilter = React.useMemo(() => {
    if (filteredBugSummaries.length > 0) {
      return filteredBugSummaries.reduce((total, summaryItem) => {
        const summaryCount = getBugSummaryCountForFilter(summaryItem, bugFilter);
        return total + (summaryCount ?? 0);
      }, 0);
    }

    if (bugFilter === 'all' && reportedTotal > 0) {
      return reportedTotal;
    }

    return filteredFileCount;
  }, [bugFilter, filteredBugSummaries, filteredFileCount, reportedTotal]);
  const totalBugsForActiveFilter = React.useMemo(() => {
    if (bugFilter === 'all' && Number(bugSummary?.totalBugCount) > 0) {
      return Number(bugSummary.totalBugCount);
    }

    if (filteredBugSummaries.length > 0) {
      return filteredBugSummaries.length;
    }

    return bugGroups.length;
  }, [bugFilter, bugGroups.length, bugSummary, filteredBugSummaries]);
  const groupCountLabel = bugFilter === 'deletions' ? 'files deleted' : 'files changed';

  const handleRefresh = React.useCallback(async () => {
    clearAlert();
    await refreshAffectedFiles();
  }, [clearAlert, refreshAffectedFiles]);

  const handleLoadMoreChanges = React.useCallback(async () => {
    clearAlert();
    await loadMoreAffectedFiles();
  }, [clearAlert, loadMoreAffectedFiles]);

  const toggleBug = React.useCallback((bugId) => {
    setExpandedBugs((current) => ({
      ...current,
      [bugId]: !current[bugId],
    }));

    setVisibleFilesByBug((current) => ({
      ...current,
      [bugId]: current[bugId] ?? INITIAL_VISIBLE_FILES,
    }));
  }, []);

  const toggleFile = React.useCallback((fileId) => {
    setExpandedFiles((current) => ({
      ...current,
      [fileId]: !current[fileId],
    }));
  }, []);

  const handleShowMoreLoadedFiles = React.useCallback((bugId, fileCount) => {
    setLoadingMoreFilesByBug((current) => ({
      ...current,
      [bugId]: true,
    }));

    window.setTimeout(() => {
      setVisibleFilesByBug((current) => ({
        ...current,
        [bugId]: Math.min(
          (current[bugId] ?? INITIAL_VISIBLE_FILES) + VISIBLE_FILES_STEP,
          fileCount
        ),
      }));

      setLoadingMoreFilesByBug((current) => ({
        ...current,
        [bugId]: false,
      }));
    }, 120);
  }, []);

  const handleLoadMoreFilesForBug = React.useCallback(
    async (groupId, bugId, fileCount) => {
      if (!bugId) {
        return;
      }

      clearAlert();
      setLoadingMoreFilesByBug((current) => ({
        ...current,
        [groupId]: true,
      }));

      try {
        await loadMoreFilesForBug({
          bugId,
          loadedCount: fileCount,
        });
      } finally {
        setLoadingMoreFilesByBug((current) => ({
          ...current,
          [groupId]: false,
        }));
      }
    },
    [clearAlert, loadMoreFilesForBug]
  );

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        clearAlert();
      }}
    >
      <div className="tw rollout-list-page">
        <AppPageSection>
          {alertMessage.message ? (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
              {alertMessage.message}
            </div>
          ) : null}

          {loading && affectedFiles.length === 0 ? (
            <FileChangeFeedSkeleton filterChipCount={4} />
          ) : (
            <div className="space-y-6">

              <FileChangeToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search... Examples: bug:13887 file:debug.js, path:customer/db, repo:SmartRP"
                onRefresh={handleRefresh}
                refreshing={loading || loadingMore || refreshing || isLoadingAnyBugFiles}
                refreshLabel="Refresh affected files"
                options={availableBugFilterOptions}
                activeOption={bugFilter}
                onOptionChange={setBugFilter}
                getOptionClassName={getBugFilterClassName}
              />

              <div className="flex flex-col gap-2 text-[12px] text-muted-foreground md:flex-row md:items-center md:justify-between">
                <span>
                  Showing {filteredFileCount} loaded files across {totalBugsForActiveFilter} bugs
                </span>
                <span>
                  Loaded {filteredFileCount} of {totalFilesForActiveFilter || filteredFileCount} files
                </span>
              </div>

              {bugGroups.length === 0 ? (
                <EmptyState
                  icon={FileDiff}
                  title={
                    affectedFiles.length === 0
                      ? 'No affected files available'
                      : 'No affected files matched your filters'
                  }
                  description={
                    affectedFiles.length === 0
                      ? 'Changed files will appear here once affected file data is available.'
                      : 'Try a different bug filter or search expression such as bug:13887, file:debug.js, or path:customer/db.'
                  }
                />
              ) : (
                <>
                  <div className="space-y-5">
                    {visibleBugGroups.map((bugGroup) => {
                      const isBugExpanded = Boolean(expandedBugs[bugGroup.id]);
                      const visibleFileLimit =
                        visibleFilesByBug[bugGroup.id] ?? INITIAL_VISIBLE_FILES;
                      const visibleFiles = bugGroup.files.slice(0, visibleFileLimit);
                      const hasMoreFiles = bugGroup.fileCount > visibleFiles.length;
                      const isLoadingMoreFiles = Boolean(loadingMoreFilesByBug[bugGroup.id]);
                      const hasAdditionalUnloadedFiles =
                        bugGroup.totalFilesForBug > bugGroup.fileCount;
                      const remainingUnloadedFiles =
                        bugGroup.totalFilesForBug - bugGroup.fileCount;

                      return (
                        <FileChangeGroupCard
                          key={bugGroup.id}
                          icon={Bug}
                          title={bugGroup.bugId}
                          linkHref={bugGroup.bugUrl}
                          linkLabel="Open bug"
                          deletionCount={bugGroup.deletionCount}
                          metaItems={[
                            {
                              icon: FolderTree,
                              label: bugGroup.repoSummary,
                            },
                            {
                              icon: UserRound,
                              label: bugGroup.authorSummary,
                            },
                            {
                              icon: Clock3,
                              label: bugGroup.latestCommitDateLabel,
                            },
                          ]}
                          countValue={bugGroup.totalFilesForBug}
                          countLabel={groupCountLabel}
                          isExpanded={isBugExpanded}
                          onToggle={() => toggleBug(bugGroup.id)}
                        >
                          <div className="border-t border-border/35 px-4 py-4">
                            <div className="flex items-center justify-between gap-3 px-2 pb-3 text-[12px] text-muted-foreground">
                              <span>
                                Showing {visibleFiles.length} loaded of {bugGroup.totalFilesForBug}{' '}
                                {bugFilter === 'deletions' ? 'matching deletions' : 'matching files'} in
                                this bug
                              </span>
                              {bugGroup.fileCount > visibleFiles.length ? (
                                <span>{bugGroup.fileCount - visibleFiles.length} more loaded hidden</span>
                              ) : hasAdditionalUnloadedFiles ? (
                                <span>{remainingUnloadedFiles} more not loaded yet</span>
                              ) : null}
                            </div>

                            {visibleFiles.map((file, index) => {
                              const actionMeta = getActionMeta(file.action);
                              const isFileExpanded = Boolean(expandedFiles[String(file.key)]);
                              const isLastVisibleRow =
                                index === visibleFiles.length - 1 && !hasMoreFiles;

                              return (
                                <FileChangeItemCard
                                  key={file.key}
                                  filePath={file.filePath}
                                  fileHref={file.fileUrl || file.bugUrl || ''}
                                  summaryMetaItems={[
                                    {
                                      icon: FolderTree,
                                      label: file.repoName,
                                    },
                                    {
                                      icon: Clock3,
                                      label: file.commitDateLabel,
                                    },
                                    {
                                      icon: UserRound,
                                      label: file.author,
                                    },
                                  ]}
                                  actionMeta={actionMeta}
                                  isExpanded={isFileExpanded}
                                  isLastVisibleRow={isLastVisibleRow}
                                  onToggle={() => toggleFile(String(file.key))}
                                  details={
                                    <>
                                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-4">
                                        <FileChangeDetailCard
                                          label="Full Path"
                                          value={file.fileUrl || file.bugUrl ? (
                                            <a
                                              href={file.fileUrl || file.bugUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="inline-flex min-w-0 cursor-pointer items-start gap-1.5 text-foreground no-underline transition-colors hover:text-primary"
                                            >
                                              <span className="min-w-0 break-all">
                                                {file.filePath || '--'}
                                              </span>
                                              <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors" />
                                            </a>
                                          ) : (
                                            file.filePath || '--'
                                          )}
                                          valueClassName="break-all"
                                        />
                                        <FileChangeDetailCard
                                          label="Revision"
                                          value={(
                                            <div className="flex min-w-0 items-start gap-2 text-[12px] leading-5 text-foreground">
                                              <GitCommitHorizontal className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                              <span className="min-w-0 break-all font-mono">
                                                {file.revision}
                                              </span>
                                            </div>
                                          )}
                                        />
                                        <FileChangeDetailCard
                                          label="Action"
                                          value={(
                                            <span
                                              className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${actionMeta.className}`}
                                            >
                                              {actionMeta.label}
                                            </span>
                                          )}
                                        />
                                        <FileChangeDetailCard
                                          label="Updated"
                                          value={file.commitDateLabel}
                                        />
                                      </div>
                                    </>
                                  }
                                />
                              );
                            })}

                            {hasMoreFiles ? (
                              <div className="border-t border-border/20 px-4 py-4">
                                <div className="flex flex-col gap-3 rounded-2xl border border-primary/20 bg-primary/6 px-4 py-3 md:flex-row md:items-center md:justify-between">
                                  <div className="space-y-1">
                                    <div className="text-[12px] font-medium text-foreground">
                                      More loaded files are ready to show in this bug
                                    </div>
                                    <div className="text-[12px] text-muted-foreground">
                                      Showing {visibleFiles.length} of {bugGroup.fileCount} loaded files.
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    className="h-10 self-start rounded-full px-4 md:self-center"
                                    onClick={() =>
                                      handleShowMoreLoadedFiles(
                                        bugGroup.id,
                                        bugGroup.fileCount
                                      )
                                    }
                                    disabled={isLoadingMoreFiles}
                                  >
                                    {isLoadingMoreFiles ? (
                                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <ChevronDown className="mr-2 h-4 w-4" />
                                    )}
                                    Show more loaded files
                                  </Button>
                                </div>
                              </div>
                            ) : hasAdditionalUnloadedFiles ? (
                              <div className="border-t border-border/20 px-4 py-4">
                                <div className="flex flex-col gap-3 rounded-2xl border border-primary/20 bg-primary/6 px-4 py-3 md:flex-row md:items-center md:justify-between">
                                  <div className="space-y-1">
                                    <div className="text-[12px] font-medium text-foreground">
                                      More matching{' '}
                                      {bugFilter === 'deletions' ? 'deletions' : 'files'} are
                                      available for this bug
                                    </div>
                                    <div className="text-[12px] text-muted-foreground">
                                      Loaded all {bugGroup.fileCount} fetched files.{' '}
                                      {remainingUnloadedFiles} more remain on the server.
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    className="h-10 self-start rounded-full px-4 md:self-center"
                                    onClick={() =>
                                      handleLoadMoreFilesForBug(
                                        bugGroup.id,
                                        bugGroup.bugId,
                                        bugGroup.fileCount
                                      )
                                    }
                                    disabled={
                                      isLoadingMoreFiles || loading || loadingMore || refreshing
                                    }
                                  >
                                    {isLoadingMoreFiles ? (
                                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <ArrowDownToLine className="mr-2 h-4 w-4" />
                                    )}
                                    Load more matching{' '}
                                    {bugFilter === 'deletions' ? 'deletions' : 'files'}
                                  </Button>
                                </div>
                              </div>
                            ) : bugGroup.fileCount > INITIAL_VISIBLE_FILES ? (
                              <div className="border-t border-border/20 px-4 py-3 text-center text-[12px] text-muted-foreground">
                                Showing all {bugGroup.fileCount} files in this bug
                              </div>
                            ) : null}
                          </div>
                        </FileChangeGroupCard>
                      );
                    })}
                  </div>

                </>
              )}

              {hasMore ? (
                <div className="flex flex-col items-center gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 rounded-xl border-border/40 bg-transparent px-5 hover:bg-accent/20"
                    onClick={handleLoadMoreChanges}
                    disabled={loading || loadingMore || refreshing || isLoadingAnyBugFiles}
                  >
                    {loadingMore ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronDown className="mr-2 h-4 w-4" />
                    )}
                    Load more changes
                  </Button>
                  <div className="text-[12px] text-muted-foreground">
                    Loaded {filteredFileCount} of {totalFilesForActiveFilter || filteredFileCount} files
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </AppPageSection>
      </div>
    </ErrorBoundary>
  );
};

export default AffectedFiles;
